import { useEffect, useLayoutEffect } from "react";
import Home from "./components/Home";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import JoinFlat from "./components/JoinFlat";
import Marketing from "./components/Marketing";
import AccountPreview from "./components/AccountPreview";
import NewFlatView from "./pages/NewFlatView";
import Support from "./components/Support";
import Privacy from "./components/Privacy";
import EULA from "./components/EULA";
import Impressum from "./components/Impressum";
import DeleteData from "./components/DeleteData";
import NotFound from "./pages/404";
import Download from "./components/Download";
import FAQ from "./components/FAQ";
import Top from "./components/Top";
import { QueryParamTracker } from "./components/QueryParamsTracker";
import BrowseFlats from "./pages/BrowseFlats";
import CreateFlat from "./pages/CreateFlat";
import EditFlat from "./pages/EditFlat";
import SidebarTabs from "./components/SidebarTabs";
import { AuthProvider } from "./contexts/AuthContext";
import { SidebarProvider, useSidebar } from "./contexts/SidebarContext";
import { UploadProvider } from "./contexts/UploadContext";
import { ProfileProvider } from "./contexts/ProfileContext";
import ChatsPlaceholder from "./pages/ChatsPlaceholder";
import ChatView from "./pages/ChatView";
import ApplicationsPlaceholder from "./pages/ApplicationsPlaceholder";
import ProfilePlaceholder from "./pages/ProfilePlaceholder";
import FlatsPlaceholder from "./pages/FlatsPlaceholder";
import SignUp from "./pages/SignUp";
import EditProfile from "./pages/EditProfile";
import CarouselScreen from "./pages/CarouselScreen";
import CityLanding from "./pages/CityLanding";
import CompleteProfileBanner from "./components/CompleteProfileBanner";
import { useProfile } from "./contexts/ProfileContext";
import { usePostHog } from "posthog-js/react";

function App() {
  const { i18n } = useTranslation();
  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    Cookies.set("language", lang, { sameSite: "None", secure: true });
  };

  useEffect(() => {
    const savedLanguage = Cookies.get("language");
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  return (
    <AuthProvider>
      <ProfileProvider>
        <SidebarProvider>
          <UploadProvider>
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <QueryParamTracker />
              <AppContent changeLanguage={changeLanguage} />
            </Router>
          </UploadProvider>
        </SidebarProvider>
      </ProfileProvider>
    </AuthProvider>
  );
}

function AppContent({
  changeLanguage,
}: {
  changeLanguage: (lang: string) => void;
}) {
  const { isCollapsed } = useSidebar();
  const { needsOnboarding } = useProfile();
  const posthog = usePostHog();
  const location = useLocation();
  const currentPath = location.pathname;
  const hideBottomTab =
    /^\/chats\/\d+/.test(currentPath) ||
    currentPath === "/create-flat" ||
    currentPath === "/signup";

  // Reset scroll on route change, except for BrowseFlats which manages its own scroll
  useLayoutEffect(() => {
    if (currentPath !== "/") {
      window.scrollTo(0, 0);
    }
  }, [currentPath]);

  // If user needs onboarding and not on signup, redirect immediately
  if (needsOnboarding && !currentPath.startsWith("/signup")) {
    posthog?.capture("onboarding_gate_redirected", {
      surface: "app_shell",
      current_path: currentPath,
      destination: "signup",
      is_authenticated: true,
    });
    window.location.replace("/signup");
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p>Setting up your account...</p>
      </div>
    );
  }

  return (
    <div
      className={`flex min-h-screen transition-all duration-300 ease-in-out ${
        hideBottomTab ? "pb-0" : "pb-20"
      } lg:pb-0 ${isCollapsed ? "lg:pl-16" : "lg:pl-56"}`}
    >
      <SidebarTabs />
      <div className="flex-1 min-h-screen flex flex-col min-w-0">
        <CompleteProfileBanner />
        <div className="flex-1 min-w-0 min-h-0 overflow-hidden flex flex-col">
          <div className="flex-1 min-h-0 flex flex-col">
            <Routes>
            <Route path="/" element={<BrowseFlats />} />
            <Route path="/chats" element={<ChatsPlaceholder />} />
            <Route path="/chats/:conversationId" element={<ChatView />} />
            <Route path="/applications" element={<ApplicationsPlaceholder />} />
            <Route path="/applications/carousel" element={<CarouselScreen />} />
            <Route path="/flats" element={<FlatsPlaceholder />} />
            <Route path="/profile" element={<ProfilePlaceholder />} />
            <Route path="/profile/edit" element={<EditProfile />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/create-flat" element={<CreateFlat />} />
            <Route
              path="/about"
              element={
                <Home
                  changeLanguage={changeLanguage}
                  child={<Top />}
                  hideHeader
                />
              }
            />
            <Route path="/app/joinflat/:token/:flatId" element={<JoinFlat />} />
            <Route path="/app/profile/:userId" element={<AccountPreview />} />
            <Route
              path="/marketing/:uuid"
              element={
                <Home
                  changeLanguage={changeLanguage}
                  child={<Marketing />}
                  hideHeader
                />
              }
            />

            <Route path="/wg-zimmer/:city" element={<CityLanding />} />
            <Route path="/app/flat-view/:flatId" element={<NewFlatView />} />
            <Route path="/app/edit-flat/:flatId" element={<EditFlat />} />
            <Route
              path="/support"
              element={<Support changeLanguage={changeLanguage} />}
            />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/eula" element={<EULA />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/delete-data" element={<DeleteData />} />
            <Route path="/download" element={<Download />} />
            <Route
              path="/faq"
              element={<FAQ changeLanguage={changeLanguage} />}
            />
            <Route path="*" element={<NotFound />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
